// @ts-nocheck
import axios from 'axios';
import { toast } from 'react-toastify';
import environment from "../config/environment";

let sessionExpiredRedirecting = false;

const ERROR_NOTIFICATION_ENDPOINT = 'errornotification/send';

/**
 * Background error-reporting call. MUST NOT use the shared axios instance,
 * because a failure here (e.g. transient 401) would otherwise trip the
 * session-expiry interceptor and log the user out. Reporting a background
 * error must never affect the user's session.
 */
async function sendErrorNotification(origin, param) {
    return axios.post(origin + '/apis/' + ERROR_NOTIFICATION_ENDPOINT, param, {
        withCredentials: true,
        responseType: 'json',
        headers: {
            'Authorization': 'Bearer ' + environment.BEARER_TOKEN,
            'X-Requested-With': 'XMLHttpRequest',
        },
    });
}

function isErrorNotificationRequest(config) {
    try {
        return !!config && typeof config.url === 'string' && config.url.includes(ERROR_NOTIFICATION_ENDPOINT);
    } catch (_) {
        return false;
    }
}

/**
 * What the error notice says about a failed request. A request that got no reply at all is reported as
 * 'Network Error', which on its own cannot tell a blocked or aborted request from a timeout, so the notice also
 * carries axios' own error code (ERR_NETWORK, ECONNABORTED, ERR_CANCELED, ERR_BAD_RESPONSE...) and whether the
 * browser thought it was online. The Apex email lists every extra key under "Request Parameters".
 */
function failureDetail(error) {
    return {
        error_status: error?.response ? error.response.status : 'Network Error',
        error_code: error?.code || '',
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    };
}

/**
 * True once the app has started sending the user to the login page after an expired session. The page is about to
 * go away and has already said why, so anything that would add a second message should stay quiet.
 */
export function isSessionExpiredRedirecting() {
    return sessionExpiredRedirecting;
}

/**
 * Exported for raw-fetch callers that bypass the interceptor below (ACOP-123 record files), so an
 * expired session behaves the same everywhere: one toast, then the login redirect.
 */
export function handleSessionExpired() {
    if (sessionExpiredRedirecting) return;
    const path = window.location.pathname;
    if (path === '/login' || path === '/') return;
    sessionExpiredRedirecting = true;
    toast.error("Your session has expired. Redirecting to login...", { autoClose: 3000 });
    // Clear the server-side session before redirecting so that /login cannot
    // immediately auto-resume with the same (possibly stale) session cookie,
    // which would otherwise create an infinite redirect loop.
    fetch(environment.BASE_URL + 'logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Authorization': 'Bearer ' + environment.BEARER_TOKEN,
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({}),
    }).catch(() => { /* best-effort */ }).finally(() => {
        window.location.href = "/login";
    });
}

axios.interceptors.response.use(
    (response) => {
        const body = response.data;
        // Never let the background error-reporting endpoint drive session state.
        if (isErrorNotificationRequest(response.config)) {
            return response;
        }
        if (body && typeof body === 'object') {
            if (body.status === 401) {
                handleSessionExpired();
                return Promise.reject({ __sessionExpired: true, response });
            }
            if (body.status === 403) {
                toast.warning(body.message || "You do not have permission to perform this action.");
            }
        }
        return response;
    },
    (error) => {
        // A failure reporting a background error must never log the user out.
        if (isErrorNotificationRequest(error.config)) {
            return Promise.reject(error);
        }
        // Only treat HTTP-level 401 as a true session expiry.
        // HTTP 403 means the server rejected the request for access reasons (not session
        // expiry) — treat it as a warning so we don't trigger a redirect loop when
        // validateEngagement denies access to a specific endpoint.
        if (error.response && error.response.status === 401) {
            handleSessionExpired();
            return Promise.reject({ __sessionExpired: true, response: error.response });
        }
        if (error.response && error.response.status === 403) {
            toast.warning("You do not have permission to perform this action.");
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);

export async function callApi(url, param) {
    param["X-Requested-With"] = "XMLHttpRequest";
    try {
        const response = await axios.get(url, {
            params: param,
            withCredentials: true,
            responseType: "json",
            headers: {
                'Authorization': 'Bearer ' + environment.BEARER_TOKEN
            }
        });

        if (response.data && response.data.DATA && response.data.DATA.registrationRequired) {
            window.location.href = "/login";
            return;
        }
        return response;
    } catch (error) {
        if (error?.__sessionExpired) return;
        console.log(error);
        let data = param.data;
        data = data ? JSON.parse(data) : {};
        let reqUrl = new URL(url);
        data['req_url'] = reqUrl.href;
        Object.assign(data, failureDetail(error));
        param.data = JSON.stringify(data);
        try {
            await sendErrorNotification(reqUrl.origin, param);
        } catch (res) {}
    }
}

export async function callApiPost(url, param) {
    param["X-Requested-With"] = "XMLHttpRequest";
    try {
        const response = await axios.post(url, param.data, {
            headers: {
                'Authorization': 'Bearer ' + environment.BEARER_TOKEN,
                'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: true,
            responseType: 'json'
        });

        if (response.data && response.data.DATA && response.data.DATA.registrationRequired) {
            window.location.href = "/login";
            return;
        }
        return response;
    } catch (error) {
        if (error?.__sessionExpired) return;
        console.log(error);
        let data = param.data;
        data = data ? JSON.parse(data) : {};
        let reqUrl = new URL(url);
        data['req_url'] = reqUrl.href;
        Object.assign(data, failureDetail(error));
        param.data = JSON.stringify(data);
        try {
            delete param.file;
            await sendErrorNotification(reqUrl.origin, param);
        } catch (res) {}
    }
}

export async function callApiPostUpload(url, param) {
    param["X-Requested-With"] = "XMLHttpRequest";
    try {
        const response = await axios.post(url, param, {
            headers: {
                'Authorization': 'Bearer ' + environment.BEARER_TOKEN,
                'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: true,
            responseType: 'json'
        });

        if (response.data && response.data.DATA && response.data.DATA.registrationRequired) {
            window.location.href = "/login";
            return;
        }
        return response;
    } catch (error) {
        if (error?.__sessionExpired) return;
        console.log(error);
        let data = param.data;
        data = data ? JSON.parse(data) : {};
        let reqUrl = new URL(url);
        data['req_url'] = reqUrl.href;
        Object.assign(data, failureDetail(error));
        param.data = JSON.stringify(data);
        try {
            delete param.file;
            await sendErrorNotification(reqUrl.origin, param);
        } catch (res) {}
    }
}

/**
 * Multipart form data upload using axios
 * Replaces jQuery's multipart AJAX functionality
 */
export async function multipartAjax(url, param, callback, errorFn, dataType) {
    try {
        const response = await axios.post(url, param, {
            headers: {
                'Authorization': 'Bearer ' + environment.BEARER_TOKEN,
                'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: true,
            timeout: 600000,
            responseType: dataType ? dataType : "json"
        });

        if (response.data && response.data.DATA && response.data.DATA.registrationRequired) {
            window.location.href = "/login";
            return;
        }

        if (callback) {
            callback(response.data);
        }
        return response;
    } catch (error) {
        if (error?.__sessionExpired) return;
        if (errorFn) {
            if (errorFn === true) {
                alert("Something went wrong!");
            } else {
                errorFn(error);
            }
        } else {
            alert(error.response?.data || error.message || "An error occurred");
        }
        throw error;
    }
}

/**
 * Get form parameters from a container element
 * Replaces jQuery's getParamObj functionality
 * @param {HTMLElement|string} container - Container element or selector
 * @returns {Object} - Object with form field values
 */
export function getParamObj(container) {
    let param = {};
    const containerEl = typeof container === 'string' ? document.querySelector(container) : container;
    if (!containerEl) return param;

    const selector = "input[type=text],input[type=tel],input[type=number],input[type=color],input[type=hidden],input[type=password],select,input[type=checkbox]:checked,input[type=radio]:checked,textarea";
    const elements = containerEl.querySelectorAll(selector);

    elements.forEach((element) => {
        const name = element.getAttribute('name');
        if (name && name.length > 0) {
            const datatype = element.getAttribute('datatype');
            let value;
            
            if (element.type === 'checkbox' || element.type === 'radio') {
                value = element.checked ? element.value : '';
            } else {
                value = element.value || '';
            }

            if (datatype === "int") {
                param[name] = value.length > 0 ? parseInt(value.trim()) : 0;
            } else {
                param[name] = value.trim();
            }
        }
    });

    return param;
}

/**
 * Show notification using react-toastify
 * Replaces jQuery's notify functionality
 * @param {Object} alertData - Alert data with msg, type, and timeout
 */
export function showNotification(alertData) {
    const options = {
        autoClose: alertData.timeout || 5000
    };

    if (alertData.type === "error") {
        toast.error(alertData.msg, options);
    } else if (alertData.type === "success") {
        toast.success(alertData.msg, options);
    } else {
        toast.warning(alertData.msg, options);
    }
}

/**
 * Show loader element
 * Note: This function manipulates DOM directly. Consider using React state management instead.
 */
export function showLoader() {
    const loader = document.getElementById("loader");
    if (loader) {
        loader.style.display = "block";
    }
}

/**
 * Hide loader element
 * Note: This function manipulates DOM directly. Consider using React state management instead.
 */
export function hideLoader() {
    const loader = document.getElementById("loader");
    if (loader) {
        loader.style.display = "none";
    }
}

/**
 * Show confirmation dialog
 * Note: This function is deprecated. Use ConfirmationModal component instead.
 * Kept for backward compatibility but should be replaced with React component.
 * @param {string} msg - Message to display
 * @param {Function} callback - Callback function to execute on confirm
 */
export function showConfirmation(msg, callback) {
    // This is a legacy function. In React, use the ConfirmationModal component instead.
    // For now, we'll use browser's confirm dialog as a fallback
    if (window.confirm(msg)) {
        callback();
    }
}

export function getUrlParameter(paramName) {
    const urlObj = new URL(window.location.href);
    return urlObj.searchParams.get(paramName);
}

export function isOpenFromAvSight() {
    const email = getUrlParameter('email');
    const hideTitle = getUrlParameter('hideTitle');
    return email && email.trim().length > 0 && hideTitle && hideTitle.trim() === 'true';
}

export function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

export function getUserEmail() {
    // SECURITY FIX: Only use session cookie for API calls, never URL parameter
    // This prevents authentication bypass attacks where users can access other users' data
    // by changing the email parameter in the URL
    const sessionEmail = getCookie('copilot');
    const urlEmail = getUrlParameter('email');
    
    // Only warn if there's a suspicious mismatch (both exist but differ)
    // Don't warn during normal pre-login state when both are empty
    if (sessionEmail && urlEmail && urlEmail !== sessionEmail) {
        console.warn(
            `SECURITY: URL email (${urlEmail}) doesn't match session (${sessionEmail}). Using session email.`
        );
    }
    
    // Return session email or null (don't log during normal pre-auth state)
    return sessionEmail || null;
}

/**
 * Email for authenticated UI + API request param when the `copilot` cookie is not yet set
 * but post-login state exists (e.g. `copilotUserEmail` in sessionStorage after verify).
 * Server still enforces session; this only avoids skipping profile fetches client-side.
 */
export function getSessionUserEmail() {
    const cookie = getUserEmail();
    if (cookie) return cookie;
    try {
        return sessionStorage.getItem('copilotUserEmail') || null;
    } catch (_) {
        return null;
    }
}

export function getAccessCode() {
    const code = getUrlParameter('code');
    if (!code || code.length === 0) {
        return getCookie('accesscode');
    }
    return code;
}

export function setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days*24*60*60*1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
}

export function deleteCookie(name) {
    document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

export function isDevMode () {
    return !environment.production;
};